import Head from 'next/head'
import Image from 'next/image'
import React, { useState } from 'react'
import type { NextPage, GetServerSideProps } from 'next'

import Modal from '../components/Modal'
import Metrics from '../components/Metrics'
import Dropdown from '../components/Dropdown'
import {
  HousingMarketResponse,
  HousingMarketSearchQuery
} from '@libs/types/generated/housing-market-ui'
import Breadcrumb from '../components/Breadcrumb'
import BuyerSeller from '../components/BuyerSeller'
import TextContent from '../components/TextContent'
import HeroImage from '../../public/hero-image.jpg'
import ArticleCards from '../components/ArticleCards'
import CityComparison from '../components/CityComparison'
import DataDisclosures from '../components/DataDisclosures'
import HousingMarketFAQ from '../components/HousingMarketFAQ'
import housingMarketService from '../services/housing-market'
import { toGeoName, convertFromKebabCase } from '../utils/formatter'

type IndexPageProps = {
  marketData: HousingMarketResponse
  coverageData: Partial<Coverage>[]
  error?: string
  regionType: HousingMarketSearchQuery['region_type']
} & Pick<HousingMarketSearchQuery, 'city' | 'state'>

const propertyTypeOptions = [
  {
    label: 'all home types',
    value: 'all-residential',
    width: 16
  },
  {
    label: 'single family homes',
    value: 'single-family-residential',
    width: 20
  },
  { label: 'condos', value: 'condo', width: 9 },
  { label: 'multi family homes', value: 'multi-family', width: 20 },
  { label: 'townhouses', value: 'townhouse', width: 13 }
]

const Index: NextPage<IndexPageProps> = ({
  marketData,
  coverageData,
  city,
  state,
  regionType,
  error
}) => {
  /* --- state --- */
  const [propertyType, setPropertyType] = useState('all-residential')

  /* --- error --- */
  if (error) {
    return (
      <div
        className="p-4"
        data-error-message={error}
        data-city={city}
        data-state={state}
        data-region-type={regionType}
      >
        Error occurred.
      </div>
    )
  }

  /* --- vars --- */
  const geo = toGeoName(marketData.context.city, marketData.context.state_code)
  const links = [
    { label: 'Overview', href: '#overview' },
    { label: 'Buyer vs. seller', href: '#buyorsell' },
    { label: 'Compare markets', href: '#compare' },
    { label: 'Find an agent', href: '#agents' },
    { label: 'Local resources', href: '#resources' }
  ]

  const titlePrefix =
    city && city !== 'Undefined'
      ? `${marketData.context.city}, ${marketData.context.state_code}`
      : `${marketData.context.state || marketData.context.state_code}`
  /* --- html --- */

  const filteredPropertyTypeOptions = propertyTypeOptions.filter(option =>
    Object(marketData.property_types.map(pt => pt.type)).includes(option.value)
  )

  return (
    <div id="housing-market-app" className="pb-16">
      <Head>
        <title>{titlePrefix + ' Housing Market | Bankrate'}</title>
      </Head>
      <div className="bg-blue-dark">
        <div className="wrapper px-4 md:px-0 pt-4 md:pt-8 pb-32 hm-wrapper-lg">
          <div className="md:-mx-4">
            <div className="md:display-flex justify-content-space-between md:flex-direction-row-reverse">
              <div className="text-right mb-4">
                <button
                  className="Button Button--bare type-body-two no-underline text-white hover:text-yellow text-crop-none"
                  aria-label="Open data disclosures modal"
                  data-target="data-disclosures-modal"
                >
                  Data disclosures
                </button>
                <Modal
                  id="data-disclosures-modal"
                  content={<DataDisclosures />}
                />
              </div>
              <Breadcrumb
                city={marketData.context.city}
                state={marketData.context.state_code}
                regionType={regionType}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="wrapper px-4">
        <div className="mx-auto hm-wrapper-lg">
          <div className="-mx-4 -mt-28 md:-mt-24">
            <img
              title="City skyline"
              className="rounded-xl md:display-none"
              src="https://toolbox-assets.qa.bankrate.com/housing-market-ui/306770d33ae37e23b744cc34ab9bce2851d4bae8/_next/static/media/hero-image.f3aa2861.jpg"
              style={{ width: '100%', height: 120 }}
            />
            <img
              title="City skyline"
              className="rounded-xl display-none md:display-grid"
              src="https://toolbox-assets.qa.bankrate.com/housing-market-ui/306770d33ae37e23b744cc34ab9bce2851d4bae8/_next/static/media/hero-image.f3aa2861.jpg"
              style={{ width: '100%', height: 360 }}
            />
          </div>
          <div className="rounded-xl bg-white text-center py-12 px-6 md:px-12 md:mx-auto -mt-16 md:-mt-24 pos-relative hm-wrapper-md">
            <h1>{marketData.market_summary.header}</h1>
            <TextContent {...marketData?.market_summary} />
            <ul className="List display-flex justify-content-center mt-12 flex-wrap -mx-6">
              {links.map(({ label, href }, i) => (
                <li
                  key={label}
                  className={`border-gray ${i > 0 ? 'border-l' : ''}`}
                >
                  <a href={href} className={`Link Link--standalone mx-4 `}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          id="overview"
          className="md:display-flex justify-content-center text-center mt-6"
        >
          <h2 className="mt-1 mb-1 mr-3 type-heading-two">
            Housing market overview for{' '}
          </h2>
          <div>
            <Dropdown
              id="house-market-overview-dropdown"
              options={filteredPropertyTypeOptions}
              selected={propertyType}
              className="mx-auto"
              selectClassName="text-2xl md:text-3xl border-l-0 border-r-0 border-t-0 border-b-2 border-blue text-blue pl-1 pr-0"
              onOptionSelect={type => setPropertyType(type)}
            />
          </div>
        </div>

        <Metrics
          geo={geo}
          propertyType={propertyType}
          marketData={marketData}
        />

        <div id="buyorsell" className="hm-wrapper-md mx-auto mt-16">
          <BuyerSeller
            geo={geo}
            summary={marketData?.buyer_or_seller_market}
            buyerText={marketData?.buying_a_house}
            sellerText={marketData?.selling_a_house}
          />
        </div>

        <div id="compare" className="hm-wrapper-md mx-auto mt-16">
          <h2 className="type-heading-two pb-2 mb-6">
            {marketData.comparables?.block?.header}
          </h2>
          <TextContent {...marketData.comparables?.block} />
          <CityComparison
            geo={geo}
            coverage={coverageData}
            comparableMarkets={marketData.comparables?.markets}
          />
        </div>

        <div id="agents" className="hm-wrapper-md mx-auto mt-16">
          <h2 className="type-heading-two pb-2 mb-6">
            {marketData.next_steps.header}
          </h2>
          <TextContent {...marketData.next_steps} />
        </div>

        <div className="hm-wrapper-md mx-auto mt-16">
          <div id="top-funnel-content-end"></div>
        </div>

        <div id="faq" className="hm-wrapper-md mx-auto mt-16">
          <HousingMarketFAQ geo={geo} marketData={marketData} />
        </div>

        {marketData?.related_articles?.length ? (
          <div id="resources" className="hm-wrapper-lg mx-auto mt-16">
            <ArticleCards articles={marketData?.related_articles} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default Index

export const getServerSideProps: GetServerSideProps<
  IndexPageProps
> = async context => {
  // fallback & clean up
  if (!context.query.state) {
    context.query.city = 'Fort Myers'
    context.query.state = 'FL'
  }
  if (!context.query.region_type) {
    context.query.region_type = context.query.city ? 'place' : 'state'
  }

  context.query.city = convertFromKebabCase(context.query.city as string)

  const disable_cache =
    context.query?.disable_cache?.toString().toLowerCase() === 'true'

  const contextQuery = {
    city: context.query.city,
    state: context.query.state,
    region_type: context.query.region_type,
    disable_cache
  } as HousingMarketSearchQuery

  try {
    // get coverage data
    const coverageRaw: Coverage[] = await housingMarketService.coverage({
      disable_cache
    })
    const coverageData = coverageRaw
      .filter(c => c.is_active === true)
      .map(c => ({
        city: c.city,
        state_code: c.state_code.toUpperCase(),
        region_type_name: c.region_type_name
      }))

    coverageData.sort((a, b) => {
      if (a.region_type_name === b.region_type_name) {
        if (a.region_type_name === 'place') {
          return a.city.localeCompare(b.city)
        }

        return a.state_code.localeCompare(b.state_code)
      }

      return a.region_type_name!.localeCompare(b.region_type_name!) * -1
    })

    // get market data
    const marketData = await housingMarketService.search(contextQuery)

    // combine final props
    const props: IndexPageProps = {
      regionType: context.query.region_type,
      state: context.query.state,
      marketData: JSON.parse(JSON.stringify(marketData)), // needed for converting date to string
      coverageData
    } as IndexPageProps

    if (context.query.city) {
      props.city = context.query.city
    }

    return { props }
  } catch (e) {
    console.error(e)
    return {
      props: {
        city: context.query.city,
        regionType: context.query.region_type,
        state: context.query.state,
        error: (e as Error).message
      } as unknown as IndexPageProps
    }
  }
}
